// src/services/WebSocketService.js

// --- Environment-Aware Configuration ---
const development = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
const wsHost = development ? '127.0.0.1:8000' : 'api.yeticlean.co.uk';
const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';

// --- User Activity WebSocket Service Class ---

class UserActivityWebSocketService {
    constructor() {
        this.socket = null;
        this.listeners = {}; // Stores event listeners: { eventName: [callback1, callback2], ... }
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
    }

    /**
     * Connects to the user-specific activity WebSocket.
     * This method is idempotent; calling it multiple times will not create new connections.
     * WebSockets are disabled in development mode.
     */
    connect() {
        // Skip WebSocket connection in development mode
        if (development) {
            console.log("UserActivityWebSocketService: WebSocket disabled in development mode.");
            return;
        }

        const actionsPath = `/ws/activity`;
        const url = `${wsProtocol}://${wsHost}${actionsPath}`;

        // If we are already connected or connecting, do nothing.
        if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
            console.log("UserActivityWebSocketService: Already connected or connecting.");
            return;
        }

        console.log(`UserActivityWebSocketService: Connecting to ${url}`);
        this.socket = new WebSocket(url);

        this.socket.onopen = () => {
            console.log("UserActivityWebSocketService: Connection established.");
            this.reconnectAttempts = 0; // Reset reconnect counter on successful connection
            this.emit('open');
        };

        this.socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                // This is the key improvement: we emit a specific event based on the 'type'
                // sent from the Django Channels consumer.
                if (data.type) {
                    this.emit(data.type, data.payload);
                } else {
                    console.warn("UserActivityWebSocketService: Received message without a 'type'", data);
                }

                // We can also emit a generic 'message' event for global listeners.
                this.emit('message', data);

            } catch (error) {
                console.error("UserActivityWebSocketService: Error parsing message data.", error);
            }
        };

        this.socket.onerror = (error) => {
            console.error("UserActivityWebSocketService: WebSocket error:", error);
            this.emit('error', error);
        };

        this.socket.onclose = (event) => {
            // 1000 is a normal closure. 4003 is our custom "Permission Denied".
            if (event.code === 4003) {
                console.warn("UserActivityWebSocketService: Connection closed by server: Permission Denied.");
            } else if (event.code !== 1000) {
                console.warn(`UserActivityWebSocketService: Connection closed unexpectedly. Code: ${event.code}`);
                this.handleReconnect();
            } else {
                console.log("UserActivityWebSocketService: Connection closed normally.");
            }
            this.socket = null; // Clear the instance on close
            this.emit('close', event);
        };
    }

    /**
     * Attempts to reconnect to the WebSocket with an exponential backoff strategy.
     */
    handleReconnect() {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            const delay = Math.pow(2, this.reconnectAttempts) * 1000; // 2s, 4s, 8s...
            console.log(`UserActivityWebSocketService: Reconnecting in ${delay / 1000}s... (Attempt ${this.reconnectAttempts})`);
            setTimeout(() => this.connect(), delay);
        } else {
            console.error("UserActivityWebSocketService: Max reconnect attempts reached. Please check the connection and refresh the page.");
        }
    }

    /**
     * Closes the WebSocket connection if it is open.
     */
    disconnect() {
        if (this.socket) {
            console.log("UserActivityWebSocketService: Disconnecting.");
            // Prevent reconnection attempts when explicitly disconnecting
            this.reconnectAttempts = this.maxReconnectAttempts;

            // Remove all event listeners to prevent memory leaks and unexpected behavior
            this.socket.onopen = null;
            this.socket.onmessage = null;
            this.socket.onerror = null;
            this.socket.onclose = null;
            this.socket.close();
            this.socket = null;
        }
    }

    // --- Pub/Sub listener methods ---

    /**
     * Subscribes a callback to an event.
     * @param {string} eventName The name of the event (e.g., 'open', 'sales_status_update', 'notification').
     * @param {function} callback The function to call when the event is emitted.
     */
    on(eventName, callback) {
        if (!this.listeners[eventName]) {
            this.listeners[eventName] = [];
        }
        this.listeners[eventName].push(callback);
    }

    /**
     * Unsubscribes a callback from an event.
     * @param {string} eventName The name of the event.
     * @param {function} callback The specific callback function to remove.
     */
    off(eventName, callback) {
        if (this.listeners[eventName]) {
            this.listeners[eventName] = this.listeners[eventName].filter(
                (listener) => listener !== callback
            );
        }
    }

    /**
     * Emits an event, calling all subscribed listeners.
     * @param {string} eventName The name of the event to emit.
     * @param {*} data The data to pass to the listeners.
     */
    emit(eventName, data) {
        if (this.listeners[eventName]) {
            this.listeners[eventName].forEach((callback) => callback(data));
        }
    }
}

// Create a singleton instance and export it.
// This ensures the entire application shares one WebSocket connection manager.
const userActivitySocketService = new UserActivityWebSocketService();
export default userActivitySocketService;